import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function FFMProduits() {
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduit, setEditingProduit] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    option_nom: '',
    option_valeurs: []
  });
  // Champ temporaire pour ajouter une valeur d'option
  const [nouvelleValeur, setNouvelleValeur] = useState('');
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    loadProduits();
  }, []);

  const loadProduits = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ffm_produits')
      .select('*')
      .order('date_creation', { ascending: false });

    if (error) {
      console.error('Erreur chargement produits FFM:', error);
    } else {
      setProduits(data || []);
    }
    setLoading(false);
  };

  const openModal = (produit = null) => {
    setErreur('');
    setNouvelleValeur('');
    if (produit) {
      setEditingProduit(produit);
      setFormData({
        nom: produit.nom,
        option_nom: produit.option_nom || '',
        option_valeurs: produit.option_valeurs || []
      });
    } else {
      setEditingProduit(null);
      setFormData({ nom: '', option_nom: '', option_valeurs: [] });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduit(null);
    setFormData({ nom: '', option_nom: '', option_valeurs: [] });
    setNouvelleValeur('');
    setErreur('');
  };

  const ajouterValeur = () => {
    const val = nouvelleValeur.trim();
    if (!val) return;
    if (formData.option_valeurs.includes(val)) {
      setErreur('Cette valeur existe déjà.');
      return;
    }
    setFormData(prev => ({
      ...prev,
      option_valeurs: [...prev.option_valeurs, val]
    }));
    setNouvelleValeur('');
    setErreur('');
  };

  const supprimerValeur = (valeur) => {
    setFormData(prev => ({
      ...prev,
      option_valeurs: prev.option_valeurs.filter(v => v !== valeur)
    }));
  };

  const handleKeyDownValeur = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      ajouterValeur();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur('');

    if (!formData.nom.trim()) {
      setErreur('Le nom du produit est obligatoire.');
      return;
    }
    if (formData.option_nom.trim() && formData.option_valeurs.length === 0) {
      setErreur('Ajoutez au moins une valeur pour l\'option.');
      return;
    }

    const payload = {
      nom: formData.nom.trim(),
      option_nom: formData.option_nom.trim() || null,
      option_valeurs: formData.option_nom.trim() ? formData.option_valeurs : []
    };

    if (editingProduit) {
      const { error } = await supabase
        .from('ffm_produits')
        .update(payload)
        .eq('id', editingProduit.id);

      if (error) {
        setErreur('Erreur lors de la modification.');
        console.error(error);
        return;
      }
    } else {
      const { error } = await supabase
        .from('ffm_produits')
        .insert([payload]);

      if (error) {
        setErreur('Erreur lors de la création.');
        console.error(error);
        return;
      }
    }

    loadProduits();
    closeModal();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    const { error } = await supabase
      .from('ffm_produits')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erreur suppression:', error);
    } else {
      loadProduits();
    }
  };

  const toggleActif = async (produit) => {
    const { error } = await supabase
      .from('ffm_produits')
      .update({ actif: !produit.actif })
      .eq('id', produit.id);

    if (error) {
      console.error('Erreur toggle actif:', error);
    } else {
      loadProduits();
    }
  };

  // ─── Styles réutilisables ────────────────────────────────────────────────────

  const styleCard = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e9ecef',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    transition: 'box-shadow 0.2s'
  };

  const styleBtnPrimary = {
    padding: '10px 20px',
    backgroundColor: '#003189',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px'
  };

  const styleBtnSecondary = {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6c757d',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500'
  };

  const styleBtnDanger = {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#dc3545',
    border: '1px solid #dc3545',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500'
  };

  const styleInput = {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const styleLabel = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057',
    marginBottom: '6px'
  };

  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#212529' }}>
            Produits FFM
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6c757d' }}>
            {produits.length} produit{produits.length > 1 ? 's' : ''} enregistré{produits.length > 1 ? 's' : ''}
          </p>
        </div>
        <button style={styleBtnPrimary} onClick={() => openModal()}>
          + Nouveau produit
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: '60px' }}>
          Chargement...
        </div>
      ) : produits.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: '60px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📦</div>
          <div style={{ fontSize: '16px', fontWeight: '600' }}>Aucun produit créé</div>
          <div style={{ fontSize: '13px', marginTop: '6px' }}>Cliquez sur "+ Nouveau produit" pour commencer.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {produits.map(produit => (
            <div key={produit.id} style={{ ...styleCard, opacity: produit.actif ? 1 : 0.55 }}>
              {/* Infos produit */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '700', color: '#212529' }}>
                    {produit.nom}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '2px 8px',
                    borderRadius: '20px',
                    backgroundColor: produit.actif ? '#d4edda' : '#f8d7da',
                    color: produit.actif ? '#155724' : '#721c24'
                  }}>
                    {produit.actif ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                {produit.option_nom ? (
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>
                      {produit.option_nom} :
                    </span>
                    {(produit.option_valeurs || []).map(val => (
                      <span key={val} style={{
                        fontSize: '12px',
                        padding: '2px 10px',
                        backgroundColor: '#e8f0fe',
                        color: '#003189',
                        borderRadius: '20px',
                        fontWeight: '500'
                      }}>
                        {val}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#adb5bd', fontStyle: 'italic' }}>
                    Pas d'option définie
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  style={styleBtnSecondary}
                  onClick={() => toggleActif(produit)}
                >
                  {produit.actif ? 'Désactiver' : 'Activer'}
                </button>
                <button
                  style={styleBtnSecondary}
                  onClick={() => openModal(produit)}
                >
                  Modifier
                </button>
                <button
                  style={styleBtnDanger}
                  onClick={() => handleDelete(produit.id)}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── MODAL CRÉATION / MODIFICATION ─────────────────────────────────────── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff', borderRadius: '16px',
            padding: '32px', width: '100%', maxWidth: '520px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 24px', fontSize: '18px', fontWeight: '700', color: '#212529' }}>
              {editingProduit ? 'Modifier le produit' : 'Nouveau produit'}
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Nom du produit */}
              <div style={{ marginBottom: '20px' }}>
                <label style={styleLabel}>Nom du produit *</label>
                <input
                  style={styleInput}
                  type="text"
                  placeholder="Ex : Maillot manches courtes"
                  value={formData.nom}
                  onChange={e => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                  autoFocus
                />
              </div>

              {/* Nom de l'option */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styleLabel}>Nom de l'option <span style={{ color: '#adb5bd', fontWeight: '400' }}>(optionnel)</span></label>
                <input
                  style={styleInput}
                  type="text"
                  placeholder="Ex : Taille, Pointure, Couleur..."
                  value={formData.option_nom}
                  onChange={e => setFormData(prev => ({ ...prev, option_nom: e.target.value, option_valeurs: e.target.value ? prev.option_valeurs : [] }))}
                />
              </div>

              {/* Valeurs de l'option */}
              {formData.option_nom.trim() && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={styleLabel}>Valeurs disponibles *</label>

                  {/* Chips des valeurs ajoutées */}
                  {formData.option_valeurs.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                      {formData.option_valeurs.map(val => (
                        <span key={val} style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '4px 12px', backgroundColor: '#e8f0fe',
                          color: '#003189', borderRadius: '20px', fontSize: '13px', fontWeight: '500'
                        }}>
                          {val}
                          <button
                            type="button"
                            onClick={() => supprimerValeur(val)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: '#003189', fontSize: '14px', lineHeight: 1, padding: 0
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Champ ajout valeur */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      style={{ ...styleInput, flex: 1 }}
                      type="text"
                      placeholder="Ex : S, M, L, XL..."
                      value={nouvelleValeur}
                      onChange={e => { setNouvelleValeur(e.target.value); setErreur(''); }}
                      onKeyDown={handleKeyDownValeur}
                    />
                    <button
                      type="button"
                      onClick={ajouterValeur}
                      style={{ ...styleBtnPrimary, padding: '10px 16px', whiteSpace: 'nowrap' }}
                    >
                      Ajouter
                    </button>
                  </div>
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#adb5bd' }}>
                    Appuyez sur Entrée ou cliquez "Ajouter" pour chaque valeur.
                  </p>
                </div>
              )}

              {/* Erreur */}
              {erreur && (
                <div style={{
                  padding: '10px 14px', backgroundColor: '#f8d7da',
                  color: '#721c24', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                }}>
                  {erreur}
                </div>
              )}

              {/* Boutons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
                <button type="button" style={styleBtnSecondary} onClick={closeModal}>
                  Annuler
                </button>
                <button type="submit" style={styleBtnPrimary}>
                  {editingProduit ? 'Enregistrer' : 'Créer le produit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FFMProduits;
